import React from 'react';
import { colors } from './theme';
import useThemeMode from './useThemeMode';

// ── light/dark switch ────────────────────────────────────────────────────────
// A single button rather than a two-option segmented control: there are only
// two states, so the control can show the state it will move to and skip the
// extra hit target. `aria-pressed` carries the current state for screen
// readers, since the visible label describes the destination, not the state.
//
// Hover lives in a stylesheet, not in onMouseEnter/onMouseLeave handlers. Those
// handlers write `element.style.color = 'var(--sn-textDim)'` directly, and an
// inline property re-assigned that way does not reliably re-resolve when the
// custom property it points at changes underneath it — the button kept
// painting the previous palette's colours for a frame or more after a switch
// while every other element updated. CSS :hover has no such problem.

const CSS = `
.sn-theme-toggle {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 36px;
  border-radius: 999px;
  border: 1px solid var(--sn-border);
  background: transparent;
  color: var(--sn-textDim);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.2px;
  cursor: pointer;
}
/* 36px square keeps the icon-only variant above the minimum target size. */
.sn-theme-toggle[data-compact='true'] { width: 36px; padding: 0; }
.sn-theme-toggle[data-compact='false'] { padding: 0 13px; }
.sn-theme-toggle:hover {
  color: var(--sn-accent);
  border-color: var(--sn-accentDim);
  background: var(--sn-accentBg);
}
`;

const SunIcon = () => (
  <svg
    aria-hidden="true"
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    // stroke as a style, not an attribute: var() and currentColor resolve in a
    // CSS property value, but a presentation attribute is parsed as SVG paint.
    style={{ stroke: 'currentColor' }}
    strokeWidth="2"
    strokeLinecap="round"
  >
    <circle cx="12" cy="12" r="4.2" />
    <path d="M12 2.4v2.2M12 19.4v2.2M4.2 4.2l1.6 1.6M18.2 18.2l1.6 1.6M2.4 12h2.2M19.4 12h2.2M4.2 19.8l1.6-1.6M18.2 5.8l1.6-1.6" />
  </svg>
);

const MoonIcon = () => (
  <svg
    aria-hidden="true"
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    style={{ stroke: 'currentColor' }}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20.5 14.6A8.6 8.6 0 1 1 9.4 3.5a6.9 6.9 0 0 0 11.1 11.1Z" />
  </svg>
);

const ThemeToggle = ({ compact = false }) => {
  const [mode, toggle] = useThemeMode();
  const goingDark = mode === 'light';

  return (
    <>
      <style>{CSS}</style>
      <button
        type="button"
        className="sn-theme-toggle"
        data-compact={String(compact)}
        onClick={toggle}
        aria-pressed={mode === 'dark'}
        aria-label={`Switch to ${goingDark ? 'dark' : 'light'} mode`}
        title={`Switch to ${goingDark ? 'dark' : 'light'} mode`}
      >
        {goingDark ? <MoonIcon /> : <SunIcon />}
        {!compact && <span>{goingDark ? 'Dark' : 'Light'}</span>}
      </button>
    </>
  );
};

export default ThemeToggle;
